<template>
  <div class="catalog-list-view">
    <div class="main-container">
        <!-- Header Bar -->
        <div class="header-bar">
          <h2 class="section-title">{{ catalogName }} Catalog</h2>
        </div>
        
        <div class="controls-bar">
          <button @click="handleCreateItem" class="action-btn" type="button" :disabled="!canCreate">
            <AppointmentIcon name="plus" /> <span>Add item</span>
          </button>
          <div class="search-filter-bars" :class="{ 'has-filters': isSuppliersCatalog }">
            <section class="search-bar" role="search" :aria-label="`${catalogName} search`">
              <AppointmentIcon name="search" class="search-icon" />
              <input
                ref="searchInput"
                type="search"
                v-model="filters.name"
                placeholder="Search items..."
                aria-label="Search items"
                autocomplete="off"
                class="header-search-input"
                data-refresh-safe
              />
            </section>
            <section v-if="isSuppliersCatalog" class="filters-bar" :aria-label="`${catalogName} filters`">
              <AppointmentIcon name="filter" class="filter-icon" />
              <div ref="engagementFilter" class="engagement-filter"
                :class="{ 'has-value': filters.engagementType }"
                @keydown.esc.stop.prevent="closeEngagementFilter(true)">
                <button ref="engagementTrigger" type="button" class="filter-label-btn"
                  aria-label="Engagement type" aria-haspopup="listbox"
                  :aria-expanded="engagementFilterOpen"
                  @click="engagementFilterOpen = !engagementFilterOpen">
                  {{ selectedEngagementLabel || 'Engagement type' }}
                </button>
                <button v-if="filters.engagementType" type="button" class="filter-chip-remove"
                  aria-label="Clear engagement type filter" @click="selectEngagementType('')">
                  <AppointmentIcon name="close" :size="14" />
                </button>
                <div v-if="engagementFilterOpen" class="engagement-dropdown" role="group" aria-label="Engagement type options">
                  <button v-for="option in engagementTypeOptions" :key="option.value" type="button"
                    class="engagement-option" :aria-pressed="filters.engagementType === option.value"
                    @click="selectEngagementType(option.value)">{{ option.label }}</button>
                </div>
              </div>
            </section>
          </div>
        </div>
    </div>

    <FloatingScrollArea class="scroll-container" :label="`${catalogName} items`">
      <div class="main-container">

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-indicator">
          Loading items...
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-message">
          Error: {{ error }}
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredItems.length === 0" class="empty-list-message">
          No items found{{ hasActiveFilters ? ' for these filters' : '' }}.
        </div>

        <!-- Items List -->
        <div v-else class="item-list">
          <div class="item-table-container">
            <table>
              <colgroup>
                <col
                  v-for="column in displayColumns"
                  :key="`col-${column.key}`"
                  class="fixed-column"
                />
                <col class="col-flex-width" />
              </colgroup>
              <tbody>
                <tr
                  v-for="item in filteredItems"
                  :key="item.id"
                  :class="{ active: item.id === activeItemId }"
                  role="button"
                  tabindex="0"
                  @click="handleRowClick(item)"
                  @keydown.enter.prevent="handleRowClick(item)"
                >
                  <td
                    v-for="column in displayColumns"
                    :key="column.key"
                    :class="['value-cell', column.class || `col-${column.key}`]"
                    :title="getColumnValue(item, column)"
                  >
                    <div
                      class="value-container"
                      :class="{ 'align-right': column.class === 'col-rate' }"
                    >
                      {{ getColumnValue(item, column) }}
                    </div>
                  </td>
                  <td class="col-flex-cell"></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="list-footer">
            <span class="item-count">{{ filteredItems.length }} items</span>
          </div>
        </div>
      </div>
    </FloatingScrollArea>

  </div>
</template>

<script>
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import FloatingScrollArea from '@/components/FloatingScrollArea.vue';
import { getCatalogTemplate, normalizeCatalogKey } from '@/constants/catalogTemplates';
import { normalizeEngagement } from '@/utils/supplierPay';

const defaultColumns = [
  { key: 'name', class: 'col-name' },
  { key: 'details', class: 'col-meta' },
  { key: 'address', class: 'col-address' },
  { key: 'rate', class: 'col-rate' },
];

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const columnDefinitions = {
  clients: [
    { key: 'name', class: 'col-name' },
    { key: 'telephone', class: 'col-phone' },
  ],
  'clients-buildings': [
    { key: 'name', class: 'col-name' },
  ],
  services: [
    { key: 'name', class: 'col-name' },
    { key: 'rate', class: 'col-rate' },
    { key: 'supplierRate', class: 'col-rate' },
    { key: 'employeeRate', class: 'col-rate' },
  ],
  suppliers: [
    { key: 'name', class: 'col-name' },
    { key: 'telephone', class: 'col-phone' },
  ],
  vendors: [
    { key: 'name', class: 'col-name' },
  ],
  cos: [
    { key: 'name', class: 'col-name' },
  ],
};

export default {
  name: 'CatalogItemList',
  components: {
    AppointmentIcon,
    FloatingScrollArea,
  },
  props: {
    catalog: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
    activeItemId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      showFiltersPanel: false,
      engagementFilterOpen: false,
      filters: {
        name: '',
        meta: '',
        address: '',
        engagementType: '',
      },
    };
  },
  computed: {
    catalogName() {
      return this.catalog?.name || 'Catalog';
    },
    catalogKey() {
      return normalizeCatalogKey(this.catalog);
    },
    catalogIdentity() {
      return `${this.catalogKey}:${this.catalog?.documentId || this.catalog?.id || ''}`;
    },
    isSuppliersCatalog() {
      return this.catalogKey === 'suppliers';
    },
    engagementTypeOptions() {
      return getCatalogTemplate('suppliers').rows
        .flatMap((row) => row.fields)
        .find((field) => field.key === 'rightToWorkEngagementType')?.options || [];
    },
    selectedEngagementLabel() {
      return this.engagementTypeOptions.find((option) => option.value === this.filters.engagementType)?.label || '';
    },
    canCreate() {
      return !!this.catalog?.id;
    },
    displayColumns() {
      return columnDefinitions[this.catalogKey] || defaultColumns;
    },
    filteredItems() {
      let filtered = this.items;

      if (this.filters.name) {
        const searchTerm = this.filters.name.toLowerCase();
        filtered = filtered.filter((item) =>
          (this.getSearchName(item) || '').toLowerCase().includes(searchTerm)
        );
      }

      if (this.filters.meta) {
        filtered = filtered.filter((item) =>
          (this.getItemMeta(item) || '').toLowerCase().includes(this.filters.meta.toLowerCase())
        );
      }

      if (this.filters.address) {
        filtered = filtered.filter((item) =>
          (item.address || '').toLowerCase().includes(this.filters.address.toLowerCase())
        );
      }

      if (this.isSuppliersCatalog && this.filters.engagementType) {
        filtered = filtered.filter((item) => normalizeEngagement(
          item.rightToWorkEngagementType ?? item.attributes?.rightToWorkEngagementType,
        ) === this.filters.engagementType);
      }

      return filtered;
    },
    activeFilterChips() {
      const chips = [];
      if (this.filters.name) {
        chips.push({ key: 'name', label: 'Name', value: this.filters.name });
      }
      if (this.filters.meta) {
        chips.push({ key: 'meta', label: 'Details', value: this.filters.meta });
      }
      if (this.filters.address) {
        chips.push({ key: 'address', label: 'Address', value: this.filters.address });
      }
      return chips;
    },
    hasActiveFilters() {
      return this.activeFilterChips.length > 0 || (this.isSuppliersCatalog && Boolean(this.filters.engagementType));
    },
  },
  watch: {
    catalogKey() {
      this.filters.engagementType = '';
      this.closeEngagementFilter();
    },
    catalogIdentity() {
      this.$nextTick(this.focusSearchInput);
    },
  },
  methods: {
    closeEngagementFilter(restoreFocus = false) {
      this.engagementFilterOpen = false;
      if (restoreFocus) this.$refs.engagementTrigger?.focus();
    },
    selectEngagementType(value) {
      this.filters.engagementType = value;
      this.closeEngagementFilter(true);
    },
    handleOutsideClick(event) {
      if (!this.$refs.engagementFilter?.contains(event.target)) this.closeEngagementFilter();
    },
    focusSearchInput() {
      const el = this.$refs?.searchInput;
      if (el?.isConnected) {
        el.focus({ preventScroll: true });
      }
    },
    getCatalogDisplayName(item) {
      const raw =
        item?.catalogDisplayName ??
        item?.attributes?.catalogDisplayName ??
        '';
      if (raw === null || raw === undefined) return '';
      return String(raw).trim();
    },
    getItemDisplayName(item) {
      return this.getCatalogDisplayName(item) || '';
    },
    getSearchName(item) {
      if (this.catalogKey !== 'clients') {
        return item?.name || item?.attributes?.name || '';
      }
      const displayName = this.getCatalogDisplayName(item);
      const name = item?.name || item?.attributes?.name || '';
      return [displayName, name].filter(Boolean).join(' ');
    },
    formatRate(rateValue) {
      if (rateValue === null || rateValue === undefined || rateValue === '') return null;
      const numeric = Number(rateValue);
      if (Number.isNaN(numeric)) return rateValue;
      return `£${numeric.toFixed(2)}`;
    },
    getItemMeta(item) {
      const segments = [];
      if (item.customerId) segments.push(`Customer ID: ${item.customerId}`);
      if (item.terms) segments.push(`Terms: ${item.terms}`);
      if (item.email) segments.push(item.email);
      if (item.telephone) segments.push(item.telephone);
      if (item.utr) segments.push(`UTR: ${item.utr}`);
      if (hasValue(item.rate)) segments.push(this.formatRate(item.rate));
      if (hasValue(item.supplierRate)) segments.push(`Subcontractor: ${this.formatRate(item.supplierRate)}`);
      if (hasValue(item.employeeRate)) segments.push(`Employee: ${this.formatRate(item.employeeRate)}`);
      if (this.catalogKey !== 'services' && hasValue(item.amount)) segments.push(`Amount: ${item.amount}`);
      return segments.filter(Boolean).join(' · ');
    },
    toggleFiltersPanel() {
      this.showFiltersPanel = !this.showFiltersPanel;
    },
    closeFiltersPanel() {
      this.showFiltersPanel = false;
    },
    clearFilter(key) {
      if (key in this.filters) {
        this.filters[key] = '';
      }
    },
    clearAllFilters() {
      this.filters.name = '';
      this.filters.meta = '';
      this.filters.address = '';
      this.filters.engagementType = '';
    },
    handleRowClick(item) {
      this.$emit('select-item', item);
    },
    handleCreateItem() {
      this.$emit('create-item');
    },
    getColumnValue(item, column) {
      if (!column?.key) return '—';
      switch (column.key) {
        case 'details':
          return this.getItemMeta(item) || '';
        case 'address':
          return item.address || '';
        case 'rate':
          return hasValue(item.rate) ? this.formatRate(item.rate) : '';
        case 'supplierRate':
        case 'employeeRate':
          return hasValue(item[column.key]) ? this.formatRate(item[column.key]) : '';
        case 'telephone':
        case 'phone':
        case 'phoneNumber':
          return item.telephone || item.phone || item.phoneNumber || '';
        case 'name':
          return item.name || item?.attributes?.name || 'Untitled item';
        default:
          return item[column.key] ?? item?.attributes?.[column.key] ?? '';
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
    this.focusSearchInput();
  },
  activated() {
    this.focusSearchInput();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  deactivated() {
    this.closeEngagementFilter();
  },
};
</script>

<style lang="sass" scoped>
@use '@/assets/styles/variables.sass' as variables

.catalog-list-view
  height: 100%
  min-height: 0
  display: flex
  flex-direction: column

  .scroll-container
    flex: 1 1 0
    min-height: 0
    width: 100%
    max-width: 1200px
    margin: 0 auto
    padding: 0

  .main-container
    margin: 0 auto
    width: 100%
    max-width: 1200px

.header-bar
  display: flex
  justify-content: space-between
  align-items: center
  gap: 16px
  margin-bottom: 16px
  padding: 12px 0
  background: transparent
  border-bottom: none
  position: static
  z-index: auto

.controls-bar
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 12px
  margin-bottom: 16px

.search-filter-bars
  display: flex
  flex: 1 1 200px
  gap: 12px
  min-width: 0
  max-width: 100%

  &.has-filters
    flex-basis: 380px

.search-bar, .filters-bar
  display: flex
  align-items: center
  flex: 1 1 0
  gap: 8px
  min-width: 0
  max-width: 100%
  min-height: 36px
  padding: 2px 12px
  box-sizing: border-box
  background: var(--noc-background, #fff)
  border: 1px solid var(--border-color)
  border-radius: 8px
  position: relative
  z-index: 2

.search-icon, .filter-icon
  color: var(--text-color-secondary)

.header-search-input
  flex: 1 1 0
  min-width: 0
  width: 100%
  box-sizing: border-box
  height: 30px
  padding: 0 4px
  border: 0
  border-radius: 4px
  outline: none
  box-shadow: none
  background: transparent
  color: var(--text-color)
  font: 400 0.875rem/1.25rem var(--font-family-base)
  cursor: text

  &:hover:not(:focus)
    background: var(--noc-background-secondary)

  &:focus
    border: 0 !important
    outline: none
    box-shadow: none
    background: var(--noc-background, #fff)

  &::placeholder
    color: var(--text-color-secondary)
    opacity: 0.7

.section-title
  color: var(--text-color)
  font-family: var(--font-family-display, 'SF Pro Display', sans-serif)
  font-size: 1.5rem
  line-height: 2rem
  font-weight: 600
  margin: 0

.engagement-filter
  position: relative
  display: inline-flex
  align-items: center
  flex: 1 1 0
  min-width: 0
  max-width: 100%
  border: 1px solid transparent
  border-radius: 6px

  &.has-value
    border-color: var(--border-color)

.filter-label-btn, .filter-chip-remove, .engagement-option
  border: 0
  border-radius: 4px
  background: transparent
  color: var(--text-color-secondary)
  font: 500 0.875rem/1.25rem var(--font-family-base)
  cursor: pointer
  touch-action: manipulation

  &:hover, &:focus-visible
    background: var(--noc-background-secondary)
    color: var(--text-color)
    outline: none

.filter-label-btn
  min-width: 0
  overflow: hidden
  text-overflow: ellipsis
  text-align: left
  min-height: 28px
  padding: 0 8px
  white-space: nowrap

.filter-chip-remove
  flex: none
  display: inline-flex
  align-items: center
  justify-content: center
  width: 26px
  min-height: 28px
  padding: 0

.engagement-dropdown
  position: absolute
  top: calc(100% + 6px)
  right: 0
  width: max-content
  min-width: 100%
  max-width: calc(100vw - 32px)
  padding: 4px
  box-sizing: border-box
  background: var(--noc-background)
  border: 1px solid var(--border-color)
  border-radius: 8px
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1)
  display: flex
  flex-direction: column
  z-index: 10

.engagement-option
  width: 100%
  padding: 8px 12px
  text-align: left
  white-space: nowrap

  &[aria-pressed='true']
    color: var(--noc-blue)
    background: var(--noc-blue-05)

.loading-indicator,
.error-message,
.empty-list-message
  padding: 24px
  text-align: center
  color: var(--text-color-secondary)

.error-message
  color: var(--noc-red)

.empty-list-message
  font-style: italic
  font-size: 0.9rem
  border: 1px dashed var(--border-color)
  border-radius: 4px
  background-color: var(--noc-background-secondary)

.item-list
  width: 100%
  margin-top: 0

.item-table-container
  width: 100%
  border: 1px solid var(--border-color)
  border-radius: 0
  background-color: var(--noc-background)
  box-shadow: none
  overflow: hidden
  overflow-x: auto

  table
    width: 100%
    border-collapse: separate
    border-spacing: 0
    table-layout: auto

    tbody
      tr
        cursor: pointer
        transition: background-color 0.12s ease

        &:hover
          background-color: var(--noc-blue-05)

        &.active
          background-color: var(--noc-blue-10)

        td
          padding: 0
          font-size: 0.9rem
          color: var(--text-color)
          vertical-align: middle
          border-bottom: 1px solid var(--border-color)

      tr:not(:last-child) td:first-child
        border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1

      tr:last-child td
        border-bottom: none

.col-flex-cell
  width: auto
  padding: 0

.fixed-column
  width: 200px

col.col-flex-width
  width: auto

.value-cell
  min-width: 200px
  width: 200px

.value-container
  padding: 12px
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap
  text-align: left
  display: flex
  align-items: center
  gap: 8px

.value-container.align-right
  text-align: right

.value-cell.col-name .value-container
  font-weight: 600

td.col-rate .value-container
  text-align: right

.action-btn
  display: inline-flex
  align-items: center
  justify-content: center
  flex: 0 0 auto
  gap: 8px
  height: 34px
  padding: 0 13px
  border: 1px solid var(--border-color)
  border-radius: 8px
  background: var(--noc-background, #fff)
  box-shadow: none
  color: var(--text-color)
  font: 500 0.875rem/1.25rem var(--font-family-base)
  white-space: nowrap
  cursor: pointer
  touch-action: manipulation

  &:hover:not(:disabled), &:focus-visible
    background: var(--noc-background-secondary)
    outline: none

  &:disabled
    opacity: 0.5
    cursor: not-allowed

@media (max-width: 480px)
  .search-filter-bars
    flex-basis: 100%

.list-footer
  margin-top: 12px
  font-size: 0.8rem
  color: var(--text-color-secondary)
  padding-left: 4px

</style>
